import { FC } from "react";
import { customerPortal } from "@vilocnv/allsetra-core";
import { useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "hooks";
import { selectDrawerSelectedAccountId } from "app/data/selectors";
import { selectGeozonesState } from "domain/geozones/store/selectors/geozones.selector";
import { selectObjectsState } from "domain/objects/store/selectors/objects.selector";
import { selectIconsState } from "domain/icons/store/selectors/icons.selector";
import { getGeozonesAction } from "domain/geozones/store/actions/get-geozones.action";
import { getGeozoneByIdAction } from "domain/geozones/store/actions/get-geozone-by-id.action";
import getIconsByCategoryAction from "domain/icons/store/actions/get-icons-by-category.actions";
import { getAlarmTypesAction } from "domain/alarms/store/actions/get-alarm-types.actions";
import { selectAlarmsState } from "domain/alarms/store/selectors/alarms.selector";
import { clearSelectedGeozone } from "domain/geozones/store/geozones.slice";
import {
  clearSelectedObject,
  setSelectedObject
} from "domain/objects/store/objects.slice";
import getObjectDataForMapAction from "domain/objects/store/actions/get-object-data-for-map.action";
import postGeozoneAction from "domain/geozones/store/actions/post-geozone.action";
import patchGeozoneAction from "domain/geozones/store/actions/patch-geozone.action";
import { SignalRService } from "app/data/services";
import { getGeozoneAssignedObjectsAction } from "domain/geozones/store/actions/get-geozone-assigned-objects.action";
import { ObjectsSearchRequestDto } from "domain/objects/entities/dtos/objects-search-reques.dto";
import getObjectsSearchAction from "domain/objects/store/actions/get-objects-search.action";
type PostGeozoneRequestDto = customerPortal.geozones.PostGeozoneRequestDto;
type PatchGeozoneRequestDto = customerPortal.geozones.PatchGeozoneRequestDto;
type ObjectForMapRequestDto = customerPortal.objects.ObjectForMapRequestDto;
const { geozones } = customerPortal;
const { GeozoneRegistrationPage: GeozoneRegistrationTemplate } = geozones;

const GeozoneRegistrationPage: FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation(["translation", "formFieldsTranslation"]);
  const dispatch = useAppDispatch();
  const accountId = useAppSelector(selectDrawerSelectedAccountId);
  const {
    geozones,
    selectedGeozone,
    isLoadingSelectedGeozone,
    geozoneObjectsMarkers,
    geozoneAssignedObjects,
    isLoadingGeozoneAssignedObjects
  } = useAppSelector(selectGeozonesState);
  const {
    markers: markersObjects,
    selectedObject,
    isLoadingObjectDetail,
    objectsSearch,
    isLoadingObjectsSearch
  } = useAppSelector(selectObjectsState);
  const { iconsByCategory, isLoadingIconsByCategory } =
    useAppSelector(selectIconsState);
  const { alarmsTypes } = useAppSelector(selectAlarmsState);

  const dispatchGetGeozones = async () => {
    return dispatch(
      getGeozonesAction({
        accountId: accountId ?? ""
      })
    );
  };

  const dispatchGetGeozoneById = (accountId: string, geozoneId: string) => {
    return dispatch(getGeozoneByIdAction({ accountId, geozoneId }));
  };

  const dispatchGetGeozoneAssignedObjects = (
    accountId: string,
    geozoneId: string
  ) => {
    return dispatch(getGeozoneAssignedObjectsAction({ accountId, geozoneId }));
  };

  const dispatchGetIconsByCategory = async () => {
    return dispatch(getIconsByCategoryAction(1));
  };

  const dispatchGetAlarmTypes = async () => {
    return dispatch(getAlarmTypesAction());
  };

  const dispatchClearSelectedGeozone = () => {
    return dispatch(clearSelectedGeozone());
  };

  const dispatchSetSelectedObject = (uniqueId: string) => {
    return dispatch(setSelectedObject({ uniqueId }));
  };

  const dispatchClearSelectedObject = () => {
    return dispatch(clearSelectedObject());
  };

  const dispatchGetObjectDataForMap = (requestData: ObjectForMapRequestDto) => {
    return dispatch(getObjectDataForMapAction(requestData));
  };

  const dispatchPostGeozone = (requestData: PostGeozoneRequestDto) => {
    return dispatch(postGeozoneAction(requestData));
  };

  const dispatchPatchGeozone = (requestData: PatchGeozoneRequestDto) => {
    return dispatch(patchGeozoneAction(requestData));
  };

  const dispatchObjectsSearch = (value: string) => {
    const requestData: ObjectsSearchRequestDto = {
      accountId: accountId ?? "",
      data: {
        itemsPerPage: 50,
        page: 1,
        where: [{ field: "name", value, type: 0 }]
      }
    };

    return dispatch(getObjectsSearchAction(requestData));
  };

  return (
    <GeozoneRegistrationTemplate
      theme={theme}
      navigate={navigate}
      t={t}
      signalRHubConnection={SignalRService.hubConnection}
      accountId={accountId}
      geozones={geozones}
      selectedGeozone={selectedGeozone}
      isLoadingSelectedGeozone={isLoadingSelectedGeozone}
      geozoneObjectsMarkers={geozoneObjectsMarkers}
      markersObjects={markersObjects}
      isLoadingObjectDetail={isLoadingObjectDetail}
      selectedObject={selectedObject}
      iconsByCategory={iconsByCategory}
      isLoadingIconsByCategory={isLoadingIconsByCategory}
      isLoadingGeozoneAssignedObjects={isLoadingGeozoneAssignedObjects}
      geozoneAssignedObjects={geozoneAssignedObjects}
      dispatchGetGeozones={dispatchGetGeozones}
      dispatchGetGeozoneById={dispatchGetGeozoneById}
      dispatchGetGeozoneAssignedObjects={dispatchGetGeozoneAssignedObjects}
      dispatchGetIconsByCategory={dispatchGetIconsByCategory}
      dispatchClearSelectedGeozone={dispatchClearSelectedGeozone}
      dispatchSetSelectedObject={dispatchSetSelectedObject}
      dispatchClearSelectedObject={dispatchClearSelectedObject}
      dispatchGetObjectDataForMap={dispatchGetObjectDataForMap}
      dispatchPostGeozone={dispatchPostGeozone}
      dispatchPatchGeozone={dispatchPatchGeozone}
      dispatchObjectsSearch={dispatchObjectsSearch}
      objectsSearch={objectsSearch}
      isLoadingObjectsSearch={isLoadingObjectsSearch}
      alarmsTypes={alarmsTypes}
      dispatchGetAlarmTypes={dispatchGetAlarmTypes}
      enableAlarmGeozone
    />
  );
};

export default GeozoneRegistrationPage;
